import axios from "axios";

const configuredBackendUrl = (process.env.REACT_APP_BACKEND_URL || "").trim();
const backendPort = process.env.REACT_APP_BACKEND_PORT || "8000";
const autoBackendUrl = `${window.location.protocol}//${window.location.hostname}:${backendPort}`;
const BACKEND_URL = !configuredBackendUrl || configuredBackendUrl === "auto"
  ? autoBackendUrl
  : configuredBackendUrl.replace(/\/+$/, "");
export const API_BASE = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

/* ------------------------------------------------------------------ *
 * JWT storage
 *
 * Both JWTs are persisted so a hard refresh can restore the session without
 * depending on third-party/cross-site cookie behaviour. The access token is
 * refreshed automatically before protected requests fail.
 * ------------------------------------------------------------------ */
const ACCESS_TOKEN_KEY = "invoiceforge.access_token";
const REFRESH_TOKEN_KEY = "invoiceforge.refresh_token";

function readStoredToken(key) {
  try {
    return window.localStorage.getItem(key) || null;
  } catch {
    return null;
  }
}

function writeStoredToken(key, value) {
  try {
    if (value) window.localStorage.setItem(key, value);
    else window.localStorage.removeItem(key);
  } catch {
    /* storage may be unavailable in private/incognito mode */
  }
}

let accessToken = readStoredToken(ACCESS_TOKEN_KEY);
let refreshToken = readStoredToken(REFRESH_TOKEN_KEY);
const listeners = new Set();

function notifyTokenListeners() {
  listeners.forEach((fn) => {
    try {
      fn(accessToken);
    } catch {
      /* a listener must never break the auth flow */
    }
  });
}

export function setAccessToken(token) {
  accessToken = token || null;
  writeStoredToken(ACCESS_TOKEN_KEY, accessToken);
  notifyTokenListeners();
}

export function setRefreshToken(token) {
  refreshToken = token || null;
  writeStoredToken(REFRESH_TOKEN_KEY, refreshToken);
}

export function setSessionTokens(data) {
  if (data?.access_token) setAccessToken(data.access_token);
  if (data?.refresh_token) setRefreshToken(data.refresh_token);
}

export function clearSessionTokens() {
  setAccessToken(null);
  setRefreshToken(null);
}

export function getAccessToken() {
  return accessToken;
}

export function getRefreshToken() {
  return refreshToken;
}

export function onTokenChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/* ------------------------------------------------------------------ *
 * Request / response interceptors
 * ------------------------------------------------------------------ */
const PUBLIC_PATHS = [
  "/auth/login",
  "/auth/register",
  "/auth/refresh",
  "/auth/verify-otp",
  "/auth/resend-otp",
];

api.interceptors.request.use((config) => {
  if (accessToken && !PUBLIC_PATHS.some((p) => config.url?.includes(p))) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

let refreshing = null;

async function refreshAccessToken() {
  if (!refreshing) {
    const config = { _skipAuthRefresh: true };
    if (refreshToken) {
      config.headers = { Authorization: `Bearer ${refreshToken}` };
    }
    refreshing = api
      .post("/auth/refresh", null, config)
      .then((res) => {
        const token = res.data?.access_token;
        if (!token) throw new Error("Refresh returned no token");
        setSessionTokens(res.data);
        return token;
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 401 || status === 403) clearSessionTokens();
        throw err;
      })
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

/**
 * Re-establish the session after a full page load.
 *
 * Tokens are persisted in localStorage, so a reload normally has an access
 * token immediately. If it has expired, the stored refresh token exchanges for
 * a new pair before the first protected request is allowed to fail.
 *
 * Resolves to true when a usable access token is in place.
 */
export async function bootstrapSession() {
  if (accessToken) return true;
  try {
    // No stored token yet: the request falls back to the HttpOnly refresh
    // cookie, which lets an existing cookie-only session migrate to tokens.
    await refreshAccessToken();
    return true;
  } catch {
    return false;
  }
}

// Notified when the session is unrecoverable, so AuthContext can flip to anon.
let onSessionExpired = () => {};
export function setSessionExpiredHandler(fn) {
  onSessionExpired = typeof fn === "function" ? fn : () => {};
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error?.response?.status;
    const isAuthRoute = PUBLIC_PATHS.some((p) => original?.url?.includes(p));

    if (
      status === 401 &&
      original &&
      !original._retried &&
      !original._skipAuthRefresh &&
      !isAuthRoute
    ) {
      original._retried = true;
      try {
        const token = await refreshAccessToken();
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      } catch {
        clearSessionTokens();
        onSessionExpired();
      }
    }
    return Promise.reject(error);
  },
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.message === "string") return detail.message;
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/**
 * Detect the structured "email not verified" response from the API.
 * Returns the detail object (with email / retry_after) or null.
 */
export function verificationRequiredFrom(err) {
  const detail = err?.response?.data?.detail;
  if (!detail || typeof detail !== "object") return null;
  if (detail.code === "email_not_verified" || detail.requires_verification) {
    return detail;
  }
  return null;
}

export const currencySymbol = (c) => (c === "USD" ? "$" : "\u20B9");
export const fmtMoney = (amount, currency = "INR") =>
  `${currencySymbol(currency)}${Number(amount || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export const DOC_TYPE_META = {
  proforma: { label: "Proforma Invoice", short: "Proforma", section: "invoice" },
  sales: { label: "Sales / Tax Invoice", short: "Sales", section: "invoice" },
  timesheet: { label: "Timesheet Invoice", short: "Timesheet", section: "invoice" },
  recurring: { label: "Recurring / Subscription", short: "Recurring", section: "invoice" },
  progress: { label: "Progress / Milestone", short: "Progress", section: "invoice" },
  commercial: { label: "Commercial Invoice", short: "Commercial", section: "invoice" },
  credit_note: { label: "Credit Note", short: "Credit Note", section: "note" },
  debit_note: { label: "Debit Note", short: "Debit Note", section: "note" },
  payment_receipt: { label: "Payment Receipt", short: "Payment", section: "receipt" },
  expense_receipt: { label: "Expense Receipt", short: "Expense", section: "receipt" },
  purchase_order: { label: "Purchase Order", short: "PO", section: "po" },
};

// Loaded lazily from GET /api/templates. Also exported as a fallback initial list.
export const TEMPLATES = [
  { id: "swiss", name: "Swiss Klein", accent: "#0055FF", base: "swiss" },
];

let _templatesCache = null;
export async function fetchTemplates() {
  if (_templatesCache) return _templatesCache;
  const { data } = await api.get("/templates");
  _templatesCache = data;
  return data;
}
